import React from 'react'
import { Accordion, AccordionSummary, AccordionDetails, Box, Typography, Divider } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { getIn, useFormikContext } from 'formik'

const PHQ9_QUESTIONS = [
  { key: 'PHQ9.QUESTION1', label: 'Little interest or pleasure in doing things' },
  { key: 'PHQ9.QUESTION2', label: 'Feeling down, depressed, or hopeless' },
  { key: 'PHQ9.QUESTION3', label: 'Trouble falling or staying asleep, or sleeping too much' },
  { key: 'PHQ9.QUESTION4', label: 'Feeling tired or having little energy' },
  { key: 'PHQ9.QUESTION5', label: 'Poor appetite or overeating' },
  {
    key: 'PHQ9.QUESTION6',
    label: 'Feeling bad about yourself, or that you are a failure, or have let yourself or your family down',
  },
  {
    key: 'PHQ9.QUESTION7',
    label: 'Trouble concentrating on things, such as reading the newspaper or watching television',
  },
  {
    key: 'PHQ9.QUESTION8',
    label:
      'Moving or speaking so slowly that other people could have noticed? Or the opposite, being so fidgety or restless that you have been moving around a lot more than usual',
  },
  { key: 'PHQ9.QUESTION9', label: 'Thoughts that you would be better off dead or of hurting yourself in some way' },
  { key: 'PHQ9.TOTAL_SCORE', label: 'Score' },
] as const

const GAD7_QUESTIONS = [
  { key: 'GAD7.QUESTION1', label: 'Feeling nervous, anxious or on edge?' },
  { key: 'GAD7.QUESTION2', label: 'Not being able to stop or control worrying?' },
  { key: 'GAD7.QUESTION3', label: 'Worrying too much about different things?' },
  { key: 'GAD7.QUESTION4', label: 'Trouble relaxing?' },
  { key: 'GAD7.QUESTION5', label: 'Being so restless that it is hard to sit still?' },
  { key: 'GAD7.QUESTION6', label: 'Becoming easily annoyed or irritable?' },
  { key: 'GAD7.QUESTION7', label: 'Feeling afraid as if something awful might happen?' },
  { key: 'GAD7.TOTAL_SCORE', label: 'Score' },
] as const

interface HealthQuestionnaireRowProps {
  label: string
  name: string
  fieldPath: string
}

const HealthQuestionnaireRow = ({ label, name, fieldPath }: HealthQuestionnaireRowProps) => {
  const { values } = useFormikContext()
  return (
    <Box sx={{ display: 'flex', paddingTop: 1, flexDirection: 'row', justifyContent: 'space-between' }}>
      <Typography sx={{ fontSize: '0.875rem' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.875rem', marginLeft: 2 }}>
        {getIn(values, `${fieldPath}.${name}`) ?? '-'}
      </Typography>
    </Box>
  )
}

interface HealthQuestionnaireProps {
  /**
   * The field path where health questionnaire data is stored in the form values.
   * For example:
   * - "REASON_FOR_SEEKING_TREATMENT.HEALTH_QUESTIONNAIRE" for comprehensive intake notes
   * - "progressAssessment.sinceLastAssessment.healthQuestionnaire" for monthly summary notes
   */
  fieldPath: string
}

export const HealthQuestionnaire = ({ fieldPath }: HealthQuestionnaireProps) => (
    <Accordion defaultExpanded={false} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" fontWeight={300}>
            Health Questionnaire
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          <Typography variant="h6">PHQ-9</Typography>
          {PHQ9_QUESTIONS.map((question) => (
            <HealthQuestionnaireRow
              key={question.key}
              label={question.label}
              name={question.key}
              fieldPath={fieldPath}
            />
          ))}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6">GAD-7</Typography>
          {GAD7_QUESTIONS.map((question) => (
            <HealthQuestionnaireRow
              key={question.key}
              label={question.label}
              name={question.key}
              fieldPath={fieldPath}
            />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
